import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

export const IMAGEFULLWIDTHBLOCK_LEGACY_FIELD_ORDER = [
  "image",
  "display_kiosk",
  "use_container",
  "height_property",
  "height",
  "link_url",
  "link_target"
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  imageFullWidthBlockDescriptor: _generatedImageFullWidthBlockDescriptor,
  imageFullWidthBlockBlocDefinition,
  imageFullWidthBlockDashboardLayout,
  imageFullWidthBlockConfig,
  imageFullWidthBlockResolveConfig,
  imageFullWidthBlockProperties,
} = doodBlockConfig(
  {
    name: "Image - Full width",
    componentType: 'ImageFullWidthBlockComponent',
    category: 'media',
    description: "Image - Full width.",
    keywords: [],
    sharedFields: {},
    /**
     * New blocs start without container/padding. Saved blocs without this key
     * keep the property default (`true`) so existing pages do not change layout.
     */
    creationDefaults: {
      use_container: false,
    },
  },

  () => ({
    image: propertyValue({
      type: 'image',
      value: '',
      options: {
        label: "pages.blocks.image-full-width.image.label",
        help: "pages.blocks.image-full-width.image.help",
      }
    }),
    display_kiosk: propertyValue({
      type: 'checkbox',
      value: false,
      options: {
        label: "pages.blocks.image-full-width.display-kiosk.label",
        help: "pages.blocks.image-full-width.display-kiosk.help",
      }
    }),
    use_container: propertyValue({
      type: 'checkbox',
      value: true,
      options: {
        label: "pages.blocks.image-full-width.use-container.label",
        help: "pages.blocks.image-full-width.use-container.help",
      }
    }),
    height_property: propertyValue({
      type: 'select',
      value: "none",
      options: {
        label: "pages.blocks.image-full-width.height-property.label",
        multiple: false,
        choices: [
          {
            value: "none",
            label: "pages.blocks.image-full-width.height-property.options.none",
          },
          {
            value: "percent",
            label: "pages.blocks.image-full-width.height-property.options.percent",
          },
          {
            value: "pixel",
            label: "pages.blocks.image-full-width.height-property.options.pixel",
          },
        ],
      }
    }),
    height: propertyValue({
      type: 'number',
      value: 100,
      options: {
        label: "pages.blocks.image-full-width.height.label",
        help: "pages.blocks.image-full-width.height.help",
      }
    }),
    link_url: propertyValue({
      type: 'link',
      value: '',
      options: {
        label: "pages.blocks.image-full-width.link-url.label",
      }
    }),
    link_target: propertyValue({
      type: 'select',
      value: "_self",
      options: {
        label: "pages.blocks.image-full-width.link-target.label",
        help: "pages.blocks.image-full-width.link-target.help",
        choices: [
          {
            value: "_self",
            label: "pages.blocks.image-full-width.link-target.options._self",
          },
          {
            value: "_blank",
            label: "pages.blocks.image-full-width.link-target.options._blank",
          },
        ],
      }
    })
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.image-full-width.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.image-full-width.fieldset.image.label",
            fields: ["image", "display_kiosk", "use_container"],
          },
          {
            legend: "pages.blocks.image-full-width.fieldset.height.label",
            fields: [
              "height_property",
              {
                prop: "height",
                visibleWhen: { height_property: "percent" },
              },
              {
                prop: "height",
                visibleWhen: { height_property: "pixel" },
              },
            ],
          },
          {
            legend: "pages.blocks.image-full-width.fieldset.link.label",
            fields: ["link_url", "link_target"],
          },
        ],
      },
    ],
  })
);

export function imageFullWidthBlockDescriptor(): IBlockDescriptor;
export function imageFullWidthBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function imageFullWidthBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedImageFullWidthBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(_generatedImageFullWidthBlockDescriptor, imageFullWidthBlockProperties, fieldOrder);
}

export {
  imageFullWidthBlockBlocDefinition,
  imageFullWidthBlockDashboardLayout,
  imageFullWidthBlockProperties,
  imageFullWidthBlockConfig,
  imageFullWidthBlockResolveConfig,
};

export type ImageFullWidthBlockConfig = typeof imageFullWidthBlockConfig;
